import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StudentReportData } from "@/services/studentReportingService";
import { reportExportUtils } from "@/utils/reportExportUtils";
import { ArrowUpDown } from "lucide-react";
import { useState } from "react";

interface AllStudentsTableProps {
  data: StudentReportData[];
  onRowClick: (studentId: string) => void;
}

export const AllStudentsTable = ({ data, onRowClick }: AllStudentsTableProps) => {
  const [sortBy, setSortBy] = useState<keyof StudentReportData>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: keyof StudentReportData) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const getCompletionBadge = (rate: number) => {
    if (rate >= 75) return <Badge className="bg-success">Excellent</Badge>;
    if (rate >= 50) return <Badge className="bg-warning">Good</Badge>;
    if (rate > 0) return <Badge className="bg-destructive">Needs Attention</Badge>;
    return <Badge variant="outline">Not Started</Badge>;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
              <div className="flex items-center gap-2">
                Student Name <ArrowUpDown className="h-4 w-4" />
              </div>
            </TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalCourses')}>
              <div className="flex items-center justify-end gap-2">
                Total Courses <ArrowUpDown className="h-4 w-4" />
              </div>
            </TableHead>
            <TableHead className="text-right">Active</TableHead>
            <TableHead className="text-right">In Progress</TableHead>
            <TableHead className="text-right">Completed</TableHead>
            <TableHead className="text-right">Certificates</TableHead>
            <TableHead className="text-right cursor-pointer" onClick={() => handleSort('averageGrade')}>
              <div className="flex items-center justify-end gap-2">
                Avg Grade <ArrowUpDown className="h-4 w-4" />
              </div>
            </TableHead>
            <TableHead className="text-right cursor-pointer" onClick={() => handleSort('completionRate')}>
              <div className="flex items-center justify-end gap-2">
                Completion Rate <ArrowUpDown className="h-4 w-4" />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground">
                No students found
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((student) => (
              <TableRow
                key={student.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(student.id)}
              >
                <TableCell className="font-medium">{student.name}</TableCell>
                <TableCell className="text-muted-foreground">{student.email}</TableCell>
                <TableCell className="text-right">{student.totalCourses}</TableCell>
                <TableCell className="text-right">{student.activeCourses}</TableCell>
                <TableCell className="text-right">{student.inProgressCourses}</TableCell>
                <TableCell className="text-right">{student.completedCourses}</TableCell>
                <TableCell className="text-right">{student.certificatesEarned}</TableCell>
                <TableCell className="text-right">
                  {reportExportUtils.formatPercentage(student.averageGrade)}
                </TableCell>
                <TableCell className="text-right">
                  {getCompletionBadge(student.completionRate)}
                  <span className="ml-2 text-muted-foreground">
                    {student.completionRate.toFixed(1)}%
                  </span>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <div className="p-4 border-t bg-muted/30">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Total Students:</span> {data.length}
            </div>
            <div>
              <span className="font-medium">Total Courses:</span>{' '}
              {data.reduce((sum, s) => sum + s.totalCourses, 0)}
            </div>
            <div>
              <span className="font-medium">Completed:</span>{' '}
              {data.reduce((sum, s) => sum + s.completedCourses, 0)}
            </div>
            <div>
              <span className="font-medium">Certificates:</span>{' '}
              {data.reduce((sum, s) => sum + s.certificatesEarned, 0)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
